import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup, Dialog } from '../components/idsk';
import { extendSession, login, logout, sessionExpiresAt } from '../auth/keycloak';

const WARN_BEFORE_MS = 2 * 60 * 1000;

/**
 * WCAG 2.2.1: warns before the SSO session (refresh token) ends and lets the user extend it.
 * keycloak-js refreshes access tokens silently while the user is active.
 */
export function SessionWarning() {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState<number | null>(null);
  const [cannotExtend, setCannotExtend] = useState(false);

  useEffect(() => {
    const tick = () => {
      const end = sessionExpiresAt();
      if (end === null) return;
      const left = end - Date.now();
      if (left <= 0) {
        void login();
        return;
      }
      setRemaining(left <= WARN_BEFORE_MS ? left : null);
    };
    tick();
    const timer = window.setInterval(tick, 10_000);
    return () => window.clearInterval(timer);
  }, []);

  const extend = async () => {
    const before = sessionExpiresAt();
    try {
      const after = await extendSession();
      if (after !== null && before !== null && after - before < 60_000) {
        setCannotExtend(true);
        return;
      }
      setRemaining(null);
      setCannotExtend(false);
    } catch {
      void login();
    }
  };

  const minutes = remaining === null ? 0 : Math.max(1, Math.ceil(remaining / 60_000));
  return (
    <Dialog
      open={remaining !== null}
      title={t('session.title')}
      role="alertdialog"
      onClose={() => setRemaining(null)}
      actions={
        <ButtonGroup>
          {!cannotExtend && <Button onClick={() => void extend()}>{t('session.extend')}</Button>}
          <Button variant="secondary" onClick={() => void logout()}>
            {t('session.logout')}
          </Button>
        </ButtonGroup>
      }
    >
      <p className="govuk-body">{t('session.body', { count: minutes })}</p>
      {cannotExtend && <p className="govuk-body">{t('session.maxReached')}</p>}
    </Dialog>
  );
}
